import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    /**
     * Hide the corner X. For a dialog the customer must not dismiss while it is working — the
     * caller then also owns refusing ESC in its `onOpenChange`.
     */
    showCloseButton?: boolean;
  }
>(({ className, children, showCloseButton = true, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      onInteractOutside={(e) => e.preventDefault()}
      className={cn(
        // `max-h` + scroll: the box is centred with `translate-y-[-50%]`, so content taller than
        // the screen is cut off at BOTH ends with nothing to scroll — unreachable, not just ugly.
        // Measured at 360x640 the OS-details dialog already reaches within 32px of the top edge.
        // With a `DialogBody` inside, the box becomes a flex column instead: header and footer
        // keep their height and only the body scrolls. A grid row would not shrink to give the
        // body a bounded height, so the switch is what makes the body the scroller. `py-3` and
        // `gap-3` go together: 12px above and below both header and footer, up to the dividers.
        "fixed left-[50%] top-[50%] z-50 grid max-h-[calc(100dvh-2rem)] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto border bg-background p-6 shadow-lg duration-200 has-data-[slot=dialog-body]:flex has-data-[slot=dialog-body]:flex-col has-data-[slot=dialog-body]:gap-3 has-data-[slot=dialog-body]:py-3 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className,
      )}
      {...props}
    >
      {children}
      {/* The icon alone gives a 16x16 tap target. Padding plus an equal negative margin grows it
          to 40x40 on touch viewports while leaving the icon exactly where it renders today. */}
      {showCloseButton && (
        <DialogPrimitive.Close className="absolute right-4 top-4 -m-3 rounded-sm p-3 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground sm:m-0 sm:p-0">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex shrink-0 flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

/**
 * The part of a dialog that scrolls. Everything between `DialogHeader` and `DialogFooter` goes in
 * here, so a dialog taller than the screen keeps its title and its buttons in view. It draws the
 * divider under the header and the one above the footer. A form that wraps the body and the footer
 * takes `DIALOG_FORM_CLASSES`, or it will not shrink with them.
 */
const DialogBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    /** Nothing follows the body: no lower divider, and it runs down to the dialog's edge. */
    withoutFooter?: boolean;
  }
>(({ className, withoutFooter = false, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="dialog-body"
    className={cn(
      // The negative margins undo the dialog's `p-6`, so the dividers and the scrollbar reach the
      // dialog's edge. A dialog with another padding passes its own pair (`-mx-5 px-5`).
      "-mx-6 min-h-0 flex-1 overflow-y-auto overscroll-contain border-t px-6 pt-4",
      withoutFooter ? "-mb-3 pb-4" : "border-b pb-4",
      className,
    )}
    {...props}
  />
));
DialogBody.displayName = "DialogBody";

/** For a `<form>` placed between the header and a `DialogBody` + `DialogFooter` it wraps. */
const DIALOG_FORM_CLASSES = "flex min-h-0 flex-1 flex-col gap-3";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      // `gap-2` rather than `sm:space-x-2`: a horizontal-only rule left the stacked buttons flush
      // against each other on mobile (measured 0px between Cancel and Create in the snapshot
      // dialog). `gap` covers both axes and yields the same 8px desktop spacing as before.
      "flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DIALOG_FORM_CLASSES,
};
